import { useEffect, useState, type FormEvent } from 'react';
import { useProjectStore } from '../stores/projectStore';
import { theme } from '../theme';

interface ProjectTemplate {
  id: string;
  name: string;
  description: string;
}

export const modalStyles = {
  overlay: {
    position: 'fixed' as const,
    inset: 0,
    background: theme.overlay,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 100,
  },
  modal: {
    width: '100%',
    maxWidth: '440px',
    background: theme.bgPanel,
    borderRadius: theme.radiusLg,
    border: `1px solid ${theme.border}`,
    padding: '32px',
  },
  title: {
    fontSize: '20px',
    fontWeight: 700,
    color: theme.textPrimary,
    marginBottom: '20px',
  },
  field: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    fontSize: '13px',
    color: theme.textSecondary,
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: theme.radiusMd,
    border: `1px solid ${theme.border}`,
    background: theme.bgInput,
    color: theme.textPrimary,
    fontSize: '14px',
    outline: 'none',
  },
  hint: {
    fontSize: '12px',
    color: theme.textMuted,
    marginTop: '4px',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '20px',
  },
  btn: {
    padding: '8px 20px',
    borderRadius: theme.radiusMd,
    border: `1px solid ${theme.border}`,
    cursor: 'pointer',
    fontSize: '14px',
    background: 'transparent',
    color: theme.textSecondary,
  },
  btnPrimary: {
    padding: '8px 20px',
    borderRadius: theme.radiusMd,
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 600,
    background: theme.accent,
    color: theme.accentText,
  },
  error: {
    color: theme.error,
    fontSize: '13px',
    marginBottom: '12px',
  },
};

export function toSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 50);
}

export function CreateProjectModal({ onClose }: { onClose: () => void }) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [autoSlug, setAutoSlug] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [templates, setTemplates] = useState<ProjectTemplate[]>([]);
  const [templateId, setTemplateId] = useState('');
  const { createProject } = useProjectStore();

  useEffect(() => {
    const token = localStorage.getItem('platform_token');
    fetch('/api/platform/templates', {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => r.json())
      .then((data: ProjectTemplate[]) => {
        setTemplates(data);
        if (data.some((t) => t.id === 'blank')) setTemplateId('blank');
        else if (data.length > 0) setTemplateId(data[0]!.id);
      })
      .catch(() => {});
  }, []);

  const handleNameChange = (val: string) => {
    setName(val);
    if (autoSlug) setSlug(toSlug(val));
  };

  const handleSlugChange = (val: string) => {
    setAutoSlug(false);
    setSlug(val.toLowerCase().replace(/[^a-z0-9-]/g, ''));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setCreating(true);
    try {
      await createProject(name, slug, templateId || undefined);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create project');
    }
    setCreating(false);
  };

  return (
    <div style={modalStyles.overlay} onClick={onClose}>
      <form style={modalStyles.modal} onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit}>
        <div style={modalStyles.title}>Create New Project</div>

        {error && <div style={modalStyles.error}>{error}</div>}

        <div style={modalStyles.field}>
          <label style={modalStyles.label}>Project Name</label>
          <input
            style={modalStyles.input}
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            required
            autoFocus
            placeholder="My Awesome RPG"
          />
        </div>

        <div style={modalStyles.field}>
          <label style={modalStyles.label}>Slug</label>
          <input
            style={modalStyles.input}
            value={slug}
            onChange={(e) => handleSlugChange(e.target.value)}
            required
            minLength={3}
            maxLength={50}
            pattern="[a-z0-9][a-z0-9-]*[a-z0-9]"
            placeholder="my-awesome-rpg"
          />
          <div style={modalStyles.hint}>Used in URLs: /play/{slug || '...'}</div>
        </div>

        {templates.length > 0 && (
          <div style={modalStyles.field}>
            <label style={modalStyles.label}>Starter Template</label>
            <select
              style={modalStyles.input}
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
            >
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <div style={modalStyles.actions}>
          <button style={modalStyles.btn} type="button" onClick={onClose}>
            Cancel
          </button>
          <button style={modalStyles.btnPrimary} type="submit" disabled={creating}>
            {creating ? 'Creating...' : 'Create Project'}
          </button>
        </div>
      </form>
    </div>
  );
}
